import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../assets/context/AuthContext';
import url from './url';
import { registerAccount } from './registerAccount';

function DraftAuthModal({ isOpen, onClose, onAuthenticated }) {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [mode, setMode] = useState('login');
    const [accountData, setAccountData] = useState({ Name: '', Email: '', Password: '' });
    const [loginData, setLoginData] = useState({ Email: '', Password: '' });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleChange = (event) => {
        const { name, value } = event.target;
        if (mode === 'login') {
            setLoginData((previous) => ({ ...previous, [name]: value }));
        } else {
            setAccountData((previous) => ({ ...previous, [name]: value }));
        }
        if (error) setError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        const email = mode === 'login' ? loginData.Email : accountData.Email;
        const password = mode === 'login' ? loginData.Password : accountData.Password;
        if (!email.trim() || !password) {
            setError(mode === 'login'
                ? 'Enter your email and password to continue.'
                : 'Enter your name, email, and password to continue.');
            return;
        }
        if (mode === 'create' && !accountData.Name.trim()) {
            setError('Enter your name, email, and password to continue.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setError('Enter a valid email address to continue.');
            return;
        }
        if (mode === 'create' && password.length < 8) {
            setError('Your password must be at least 8 characters.');
            return;
        }

        setIsSubmitting(true);
        try {
            let data;
            if (mode === 'create') {
                data = await registerAccount(accountData);
            } else {
                const response = await fetch(`${url}/api/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(loginData),
                });
                data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || 'We could not sign you in.');
                }
            }

            login(data.token, data.Cust_Id);
            await onAuthenticated(data.Cust_Id);
        } catch (submitError) {
            setError(submitError.message || 'We could not save your draft. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const switchMode = () => {
        setMode((previous) => previous === 'login' ? 'create' : 'login');
        setError('');
    };

    return (
        <div className="cw-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !isSubmitting && onClose()}>
            <div className="cw-modal cw-account-modal" role="dialog" aria-modal="true" aria-labelledby="draft-auth-modal-title">
                <div className="cw-modal-header">
                    <div>
                        <span className="section-kicker">{mode === 'login' ? 'WELCOME BACK' : 'SAVE YOUR PROGRESS'}</span>
                        <h2 id="draft-auth-modal-title">
                            {mode === 'login' ? <>Sign in<br /><em>to save it.</em></> : <>Create your account<br /><em>to save it.</em></>}
                        </h2>
                    </div>
                    <button type="button" className="cw-modal-close" onClick={onClose} aria-label="Close dialog" disabled={isSubmitting}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="cw-modal-body">
                        <p className="cw-modal-copy">Your draft will be saved securely to your account. You can return to it whenever you’re ready.</p>
                        {mode === 'login' ? (
                            <div className="cw-form-grid">
                                <label className="cw-form-span-2">Email address<input type="email" name="Email" placeholder="you@example.com" value={loginData.Email} onChange={handleChange} autoFocus /></label>
                                <label className="cw-form-span-2">Password<input type="password" name="Password" placeholder="Your password" value={loginData.Password} onChange={handleChange} /></label>
                                <button type="button" className="review-inline-link auth-modal-reset" onClick={() => { onClose(); navigate('/ForgotPassword'); }}>Forgot password?</button>
                            </div>
                        ) : (
                            <div className="cw-form-grid">
                                <label className="cw-form-span-2">Your name<input type="text" name="Name" placeholder="Full name" value={accountData.Name} onChange={handleChange} autoFocus /></label>
                                <label className="cw-form-span-2">Email address<input type="email" name="Email" placeholder="you@example.com" value={accountData.Email} onChange={handleChange} /></label>
                                <label className="cw-form-span-2">Password<input type="password" name="Password" placeholder="At least 8 characters" value={accountData.Password} onChange={handleChange} minLength="8" /></label>
                            </div>
                        )}
                        {error && <p className="account-error" role="alert">{error}</p>}
                        <p className="cw-login-prompt">
                            {mode === 'login' ? 'New to CourierLink?' : 'Already have an account?'}{' '}
                            <button type="button" className="review-inline-link" onClick={switchMode}>
                                {mode === 'login' ? 'Create an account' : 'Log in instead'}
                            </button>
                        </p>
                    </div>
                    <div className="cw-modal-footer">
                        <button type="button" className="btn btn-stroked" onClick={onClose} disabled={isSubmitting}>Not yet</button>
                        <button type="submit" className="btn btn-rnd" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving draft…' : (mode === 'login' ? 'Sign in & save draft' : 'Create & save draft')} <span aria-hidden="true">↗</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default DraftAuthModal;